import React, { useEffect } from 'react';
import {
  Button,
  ButtonGroup,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  CardHeader,
} from 'reactstrap';
import { useNotifications } from '@/hooks';
import { Form, Field } from '@availity/form';
import * as yup from 'yup';
import { useMutation, useQuery } from '@apollo/react-hooks';
import shareIntegrationQuery from '@/graphql/mutations/shareVendorIntegration';
import { nodeQuery } from '@/graphql/queries';

export interface ShareEmailModalProps {
  dismiss: () => void;
  isOpen: boolean;
  id: string;
}

const ShareEmailModal: React.SFC<ShareEmailModalProps> = ({
  dismiss,
  isOpen,
  id,
}) => {
  const { data } = useQuery(nodeQuery, {
    fetchPolicy: 'cache-only',
    variables: {
      id,
    },
  });
  const vendorCampaign = data?.node;
  const { create: createNotification } = useNotifications();
  const [shareIntegration, { loading: sending }] = useMutation(
    shareIntegrationQuery,
    {
      onCompleted: () => {
        createNotification({
          message: `Email successfully sent!`,
          type: 'alert',
          action: 'MESSAGE',
          color: 'success',
        });
        dismiss();
      },
      onError: (error) =>
        createNotification({
          message: error.message,
          type: 'alert',
          action: 'MESSAGE',
          color: 'danger',
        }),
    }
  );

  return (
    <Modal isOpen={isOpen} toggle={dismiss}>
      <Form
        initialValues={{
          email: '',
        }}
        onSubmit={(values) => {
          const { layerID, vendorID, vendorSourceCampaign } = vendorCampaign;
          // send email
          shareIntegration({
            variables: {
              input: {
                toAddresses: [values.email],
                templateName: 'vendorIntegrationNew',
                templateData: {
                  layerID,
                  vendorID,
                  vendorSourceCampaign,
                },
              },
            },
          });
        }}
        validationSchema={yup.object().shape({
          email: yup
            .string()
            .email('Must be a valid email address')
            .required('This field is required.'),
        })}
      >
        {({ resetForm }) => {
          // eslint-disable-next-line react-hooks/rules-of-hooks
          useEffect(() => {
            if (isOpen) {
              return () => resetForm();
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
          }, [isOpen, resetForm]);

          return (
            <>
              <ModalHeader wrapTag={CardHeader} toggle={dismiss}>
                Share Integration Email
              </ModalHeader>
              <ModalBody>
                <p className="text-muted">
                  We sent an email with instructions on how to establish the
                  link with your vendor to the creator of this vendor campaign
                  when it was first created. You may send another copy of these
                  instructions using this form.
                </p>
                <Field
                  name="email"
                  label="Email Address"
                  placeholder="example@email.com"
                  type="email"
                />
              </ModalBody>
              <ModalFooter>
                <ButtonGroup>
                  <Button disabled={!sending} onClick={dismiss}>
                    Dismiss
                  </Button>
                  <Button color="success" disabled={sending} type="submit">
                    Send Email
                  </Button>
                </ButtonGroup>
              </ModalFooter>
            </>
          );
        }}
      </Form>
    </Modal>
  );
};

export default ShareEmailModal;
